import React, { createContext, useContext, useEffect, useReducer, ReactNode } from 'react';
import { TelegramAuthService, AuthErrorCode } from '../services/auth';
import { TelegramService } from '../services/telegram';
import type { AuthContext, DatabaseUser, TelegramUser } from '../types';

// Auth state interface
interface AuthState {
  user: DatabaseUser | null;
  telegramUser: TelegramUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
}

// Auth actions
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: DatabaseUser; telegramUser: TelegramUser } }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_SIGNOUT' }
  | { type: 'AUTH_REFRESH_START' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_INITIALIZED' };

// Initial state
const initialState: AuthState = {
  user: null,
  telegramUser: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  isInitialized: false,
};

// Auth reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        telegramUser: action.payload.telegramUser,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        isInitialized: true,
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        user: null,
        telegramUser: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
        isInitialized: true,
      };
    case 'AUTH_SIGNOUT':
      return {
        ...state,
        user: null,
        telegramUser: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'AUTH_REFRESH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'SET_INITIALIZED':
      return {
        ...state,
        isInitialized: true,
        isLoading: false,
      };
    default:
      return state;
  }
}

// Create context
const AuthContextInstance = createContext<AuthContext | undefined>(undefined);

// Provider props
interface TelegramAuthProviderProps {
  children: ReactNode;
}

// Provider component
export function TelegramAuthProvider({ children }: TelegramAuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  
  const authService = TelegramAuthService.getInstance();
  const telegramService = TelegramService.getInstance();

  // Initialize authentication on mount
  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        dispatch({ type: 'AUTH_START' });

        // Initialize Telegram service first
        await telegramService.initialize();

        // Check if we have a valid session
        const isValidSession = await authService.validateSession();
        if (isValidSession) {
          const currentUser = authService.getCurrentUser();
          const telegramUser = telegramService.getTelegramUser();
          
          if (currentUser && telegramUser && isMounted) {
            dispatch({ 
              type: 'AUTH_SUCCESS', 
              payload: { user: currentUser, telegramUser } 
            });
            return;
          }
        }

        // Attempt authentication
        const authResult = await authService.authenticate();
        
        if (!isMounted) return;

        if (authResult.success && authResult.user) {
          const telegramUser = telegramService.getTelegramUser();
          if (telegramUser) {
            dispatch({ 
              type: 'AUTH_SUCCESS', 
              payload: { user: authResult.user, telegramUser } 
            });
          } else {
            dispatch({ 
              type: 'AUTH_ERROR', 
              payload: 'Не удалось получить данные пользователя Telegram' 
            });
          }
        } else {
          // Handle specific error cases
          const errorMessage = getErrorMessage(authResult.errorCode, authResult.error);
          dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
        }
      } catch (error) {
        console.error('Authentication initialization error:', error);
        if (isMounted) {
          dispatch({ 
            type: 'AUTH_ERROR', 
            payload: 'Неожиданная ошибка при инициализации аутентификации' 
          });
        }
      }
    };

    initializeAuth();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, []);

  // Session validation interval
  useEffect(() => {
    if (!state.isAuthenticated || !state.isInitialized) {
      return;
    }

    // Validate session every 5 minutes
    const interval = setInterval(async () => {
      try {
        const isValid = await authService.validateSession();
        if (!isValid) {
          console.warn('Session validation failed, signing out user');
          dispatch({ type: 'AUTH_SIGNOUT' });
        }
      } catch (error) {
        console.error('Session validation error:', error);
        // Don't sign out on validation errors, just log them
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [state.isAuthenticated, state.isInitialized]);

  // Sign out function
  const signOut = async (): Promise<void> => {
    try {
      await authService.signOut();
      dispatch({ type: 'AUTH_SIGNOUT' });
    } catch (error) {
      console.error('Sign out error:', error);
      // Force sign out even if service call fails
      dispatch({ type: 'AUTH_SIGNOUT' });
    }
  };

  // Refresh session function
  const refreshSession = async (): Promise<void> => {
    try {
      dispatch({ type: 'AUTH_REFRESH_START' });
      
      const authResult = await authService.refreshSession();
      
      if (authResult.success && authResult.user) {
        const telegramUser = telegramService.getTelegramUser();
        if (telegramUser) {
          dispatch({ 
            type: 'AUTH_SUCCESS', 
            payload: { user: authResult.user, telegramUser } 
          });
        } else {
          dispatch({ 
            type: 'AUTH_ERROR', 
            payload: 'Не удалось получить данные пользователя Telegram' 
          });
        }
      } else {
        const errorMessage = getErrorMessage(authResult.errorCode, authResult.error);
        dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
      }
    } catch (error) {
      console.error('Session refresh error:', error);
      dispatch({ 
        type: 'AUTH_ERROR', 
        payload: 'Ошибка при обновлении сессии' 
      });
    }
  };

  // Clear error function (useful for retry scenarios)
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Context value
  const contextValue: AuthContext = {
    user: state.user,
    telegramUser: state.telegramUser,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    signOut,
    refreshSession,
  };

  return (
    <AuthContextInstance.Provider value={contextValue}>
      {children}
    </AuthContextInstance.Provider>
  );
}

// Hook to use auth context
export function useAuth(): AuthContext {
  const context = useContext(AuthContextInstance);
  if (context === undefined) {
    throw new Error('useAuth must be used within a TelegramAuthProvider');
  }
  return context;
}

// Helper function to get user-friendly error messages
function getErrorMessage(errorCode?: AuthErrorCode, fallbackError?: string): string {
  if (!errorCode) {
    return fallbackError || 'Неизвестная ошибка аутентификации';
  }

  const authService = TelegramAuthService.getInstance();
  return authService.getErrorMessage(errorCode);
}

// Export context for testing purposes
export { AuthContextInstance };